import { useState } from "react";
import { tradeService } from "../../services/tradeService";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Trade } from "./TradeTable";
import { AlertTriangle } from "lucide-react";

interface DeleteTradeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trade: Trade | null;
}

export function DeleteTradeDialog({
  isOpen,
  onClose,
  onSuccess,
  trade,
}: DeleteTradeDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!trade) return;

    setIsLoading(true);
    setError(null);

    try {
      await tradeService.deleteTrade(trade._id);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Delete trade error:", err);
      // Backend returns 400 if trade has courses
      if (err.response?.status === 400) {
        setError(
          err.response.data.message ||
            "Cannot delete trade. It might have linked courses."
        );
      } else {
        setError("Failed to delete trade. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!trade) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Trade">
      <div className="space-y-4">
        {error ? (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg border border-red-100">
            {error}
          </div>
        ) : (
          <div className="flex gap-4 p-4 bg-amber-50 rounded-lg text-amber-900">
            <AlertTriangle className="w-6 h-6 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold mb-1">
                Warning: This action cannot be undone.
              </p>
              <p>
                Are you sure you want to delete{" "}
                <span className="font-bold">{trade.name}</span>?
              </p>
            </div>
          </div>
        )}

        <p className="text-sm text-gray-500">
          Ensure this trade is not assigned to any active courses before
          deleting.
        </p>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            {isLoading ? "Deleting..." : "Delete Trade"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
